import type { UseFernieChatReturn } from "~/hooks/useFernieChat";

import { Box, Button, Menu, MenuItem, Paper } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import FernieConversation from "~/components/fernie/FernieConversation";
import FernieInlineLockup from "~/components/fernie/FernieInlineLockup";
import FernieLockup from "~/components/fernie/FernieLockup";
import { BodyText, BodyTextSmall, CaptionText, TitleText } from "~/components/Typographies";
import { OnboardingJourneyNextActionKind } from "~/gql/graphqlBase.generated";
import onboardingWelcome, {
	type JourneyProgressSummary,
	type WelcomeAudience,
	type WelcomeFamilyStatus
} from "~/helpers/onboardingWelcome";
import useOnboardingJourneyMutations from "~/hooks/mutations/useOnboardingJourneyMutations";
import useOnboardingWelcomeMutations from "~/hooks/mutations/useOnboardingWelcomeMutations";
import useFernieWelcome from "~/hooks/useFernieWelcome";

interface FernieHomeWindowProps {
	chat: UseFernieChatReturn;
}

const FAMILY_STATUS_KEY: Record<WelcomeFamilyStatus, string> = {
	deferred: "home.fernieWelcome.familyStatus.deferred",
	done: "home.fernieWelcome.familyStatus.done",
	inProgress: "home.fernieWelcome.familyStatus.inProgress",
	notChosen: "home.fernieWelcome.familyStatus.notChosen"
};

const NEXT_STEP_KEY: Record<OnboardingJourneyNextActionKind, string> = {
	[OnboardingJourneyNextActionKind.AnswerQuestion]: "home.fernieWelcome.nextStep.answerQuestion",
	[OnboardingJourneyNextActionKind.RecoverArtifact]: "home.fernieWelcome.nextStep.recoverArtifact",
	[OnboardingJourneyNextActionKind.ReviewPortion]: "home.fernieWelcome.nextStep.reviewPortion",
	[OnboardingJourneyNextActionKind.SatisfyDependency]: "home.fernieWelcome.nextStep.satisfyDependency",
	[OnboardingJourneyNextActionKind.SelectScope]: "home.fernieWelcome.nextStep.selectScope",
	[OnboardingJourneyNextActionKind.VerifySelectedWork]: "home.fernieWelcome.nextStep.verifySelectedWork"
};

const AUDIENCE_TITLE_KEY: Record<WelcomeAudience, string> = {
	continue: "home.fernieWelcome.continueTitle",
	new: "home.fernieWelcome.newTitle",
	status: "home.fernieWelcome.statusTitle"
};

const PROGRESS_COUNTS: {
	key: keyof Pick<JourneyProgressSummary, "accepted" | "loaded" | "pending">;
	labelKey: string;
}[] = [
	{ key: "loaded", labelKey: "home.fernieWelcome.progress.loaded" },
	{ key: "accepted", labelKey: "home.fernieWelcome.progress.accepted" },
	{ key: "pending", labelKey: "home.fernieWelcome.progress.pending" }
];

const FernieHomeWindow = ({ chat }: FernieHomeWindowProps) => {
	const { t } = useTranslation("common");
	const welcome = useFernieWelcome();
	const { isStartingJourney, startJourney } = useOnboardingJourneyMutations();
	const { dismissWelcome, snoozeWelcome } = useOnboardingWelcomeMutations();
	const [choicesAnchor, setChoicesAnchor] = useState<HTMLElement | null>(null);

	if (!welcome) return null;

	const opening = onboardingWelcome.welcomeOpening(welcome.audience);
	const { progress } = welcome;

	const handleOpen = async () => {
		if (opening.startsJourney && !(await startJourney())) return;
		await chat.send(t(opening.messageKey), { opensOnboarding: true });
	};

	const handleChoice = (choose: () => Promise<boolean>) => {
		setChoicesAnchor(null);
		void choose();
	};

	const statusExplanation = <BodyText sx={{ mt: 1 }}>{t("home.fernieWelcome.statusIntro")}</BodyText>;

	const setupExplanation = (
		<>
			<BodyText sx={{ mt: 1 }}>{t("home.fernieWelcome.intro")}</BodyText>
			<BodyTextSmall sx={{ color: "text.secondary", display: "block", mt: 1 }}>
				{t("home.fernieWelcome.families")}
			</BodyTextSmall>
			<BodyTextSmall sx={{ color: "text.secondary", display: "block", mt: 1 }}>
				{t("home.fernieWelcome.noFiles")}
			</BodyTextSmall>
		</>
	);

	const explanation = welcome.audience === "status" ? statusExplanation : setupExplanation;

	const progressSummary = progress && (
		<>
			<Box sx={{ display: "flex", gap: 1, justifyContent: "center", mt: 2 }}>
				{PROGRESS_COUNTS.map(({ key, labelKey }) => (
					<Box
						key={key}
						sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, minWidth: 88, px: 1.5, py: 1 }}
					>
						<TitleText>{progress[key]}</TitleText>
						<CaptionText sx={{ color: "text.secondary" }}>{t(labelKey)}</CaptionText>
					</Box>
				))}
			</Box>
			{progress.openQuestions > 0 && (
				<BodyTextSmall sx={{ display: "block", mt: 1.5 }}>
					{t("home.fernieWelcome.openQuestions", { count: progress.openQuestions })}
				</BodyTextSmall>
			)}
			{progress.nextStep && (
				<BodyTextSmall sx={{ display: "block", fontWeight: 600, mt: 1 }}>
					{t("home.fernieWelcome.nextStepLabel", { step: t(NEXT_STEP_KEY[progress.nextStep]) })}
				</BodyTextSmall>
			)}
			<Box component="ul" sx={{ listStyle: "none", m: 0, mt: 1.5, p: 0 }}>
				{progress.families.map(({ family, status }) => (
					<Box
						component="li"
						key={family}
						sx={{ display: "flex", gap: 1, justifyContent: "space-between", maxWidth: 360, mx: "auto", py: 0.25 }}
					>
						<BodyTextSmall>{t(onboardingWelcome.familyLabelKeys[family])}</BodyTextSmall>
						<BodyTextSmall sx={{ color: "text.secondary" }}>{t(FAMILY_STATUS_KEY[status])}</BodyTextSmall>
					</Box>
				))}
			</Box>
		</>
	);

	const welcomeState = (
		<Box sx={{ maxWidth: 560, mx: "auto", px: 1, py: 4, textAlign: "center" }}>
			<FernieLockup nameColor="text.primary" size={56} state="rest" sx={{ mb: 2 }} />
			<TitleText>{t(AUDIENCE_TITLE_KEY[welcome.audience])}</TitleText>
			{progressSummary || explanation}
			<Button
				disabled={isStartingJourney || chat.loading}
				onClick={handleOpen}
				size="large"
				sx={{ mt: 3 }}
				variant="contained"
			>
				{t(opening.actionLabelKey)}
			</Button>
		</Box>
	);

	return (
		<Paper
			aria-label={t("home.fernieWelcome.windowLabel")}
			component="section"
			sx={{
				backgroundColor: "background.default",
				border: "1px solid",
				borderColor: "divider",
				borderRadius: 2.75,
				display: "flex",
				flexDirection: "column",
				height: { md: 560, xs: "70vh" },
				maxWidth: 880,
				mx: "auto",
				overflow: "hidden",
				width: "100%"
			}}
			variant="outlined"
		>
			<Box
				sx={{
					alignItems: "center",
					borderBottom: "1px solid",
					borderColor: "divider",
					display: "flex",
					justifyContent: "space-between",
					px: 2,
					py: 1
				}}
			>
				<FernieInlineLockup size={26} state="rest" />
				<Button
					aria-haspopup="menu"
					color="inherit"
					onClick={(event) => setChoicesAnchor(event.currentTarget)}
					size="small"
				>
					{t("home.fernieWelcome.notNow")}
				</Button>
				<Menu anchorEl={choicesAnchor} onClose={() => setChoicesAnchor(null)} open={choicesAnchor !== null}>
					<MenuItem onClick={() => handleChoice(snoozeWelcome)}>{t("home.fernieWelcome.remindLater")}</MenuItem>
					<MenuItem onClick={() => handleChoice(dismissWelcome)}>{t("home.fernieWelcome.dontShowAgain")}</MenuItem>
				</Menu>
			</Box>
			<FernieConversation chat={chat} emptyState={welcomeState} />
		</Paper>
	);
};

export default FernieHomeWindow;
